import { configureStore } from "@reduxjs/toolkit";
import workflowReducer, { workflowSlice } from "./workflowSlice";
import uiSlice from "./uiSlice";
import viewsSlice from "./viewsSlice";
import aiBuilderSlice from "./aiBuilderSlice";

// ─── Re-export workflow actions (barrel for the slice's importers) ────────
// NOTE: destructure `.actions` at runtime (as with the ui/views/ai slices
// below) rather than `export { … } from "./workflowSlice"`. Webpack cannot
// statically trace names created by the slice's destructuring export
// (`export const { setGraph } = workflowSlice.actions`) through a re-export,
// so the static form silently drops every workflow action from this barrel.

export const {
  resetWorkflow,
  setGraph,
  undo,
  redo,
  setNodes,
  setEdges,
  graphNodesChanged,
  graphEdgesChanged,
  setActivities,
  addActivity,
  addNode,
  updateNodeData,
  deleteNode,
  updateEdgeData,
  addFormToState,
  addExistingActivitiesToState,
  deleteForm,
  markActivitiesSaved,
  setSelectedNode,
  setSelectedEdge,
  setWorkflow,
  updateWorkflowName,
  setWorkflowDataFields,
  addWorkflowDataField,
  updateWorkflowDataField,
  deleteWorkflowDataField,
  reorderWorkflowDataFields,
  insertWorkflowDataFieldAfter,
  setActivityPermissions,
  updateActivityPermission,
  setActivityDataFields,
  setActivityDataFieldsForActivity,
  addActivityDataField,
  insertActivityDataFieldAfter,
  updateActivityDataField,
  deleteActivityDataField,
  reorderActivityDataFields,
  setCurrentOrg,
  setCurrentPipelineGroup,
  setActivityTriggers,
  setActivityTriggerFlow,
  setActivityTriggerSettings,
  setActivityPrefills,
  setActivityPrefillFlow,
} = workflowSlice.actions;

// ─── Export UI actions ────────────────────────────────────────────────────

export const {
  setTheme,
  setSelectedActivityUid,
  setHoveredActivity,
  setHoveredEdgeId,
  setSearchHighlight,
  openAddActivityModal,
  closeAddActivityModal,
  openExistingActivityModal,
  closeExistingActivityModal,
  openConfirmation,
  closeConfirmation,
  showToast,
  hideToast,
  openJsonModal,
  closeJsonModal,
  openTriggerFlowPopup,
  closeTriggerFlowPopup,
  setIsDirty,
  addDirtyOwner,
  removeDirtyOwner,
  clearAllDirty,
  setActiveView,
  setBreadcrumbs,
  setTargetMode,
} = uiSlice.actions;

// ─── Export views actions ─────────────────────────────────────────────────

export const {
  setRecordViews,
  addRecordView,
  updateRecordViewInState,
  deleteRecordViewInState,
  setSelectedRecordView,
  setDetailViews,
  addDetailView,
  updateDetailViewInState,
  deleteDetailViewInState,
  setSelectedDetailView,
  setActivityViews,
  addActivityView,
  updateActivityViewInState,
  deleteActivityViewInState,
  setSelectedActivityView,
  setChartTemplates,
  addChartTemplate,
  updateChartTemplateInState,
  deleteChartTemplateInState,
  setSelectedChartTemplate,
  setChartScreens,
  addChartScreen,
  updateChartScreenInState,
  deleteChartScreenInState,
  setSelectedChartScreen,
  setMcScreens,
  addMcScreen,
  updateMcScreenInState,
  deleteMcScreenInState,
  setSelectedMcScreen,
  setScreens,
  addScreen,
  updateScreenInState,
  deleteScreenInState,
  setSelectedScreen,
  setDvScreens,
  addDvScreen,
  updateDvScreenInState,
  deleteDvScreenInState,
  setSelectedDvScreen,
  setRvScreens,
  addRvScreen,
  updateRvScreenInState,
  deleteRvScreenInState,
  setSelectedRvScreen,
  setAvScreens,
  addAvScreen,
  updateAvScreenInState,
  deleteAvScreenInState,
  setSelectedAvScreen,
  setHeaderConfigs,
  addHeaderConfig,
  updateHeaderConfigInState,
  deleteHeaderConfigInState,
  setSelectedHeaderConfig,
  setReactComponents,
  setSelectedReactComponent,
  addReactComponent,
  updateReactComponentInState,
  removeReactComponent,
  setFormScreen,
  setSelectedFormDevice,
} = viewsSlice.actions;

// ─── Export AI builder actions ────────────────────────────────────────────

export const {
  setSession: aiSetSession,
  setMessages: aiSetMessages,
  addMessage: aiAddMessage,
  setNodes: aiSetNodes,
  setEdges: aiSetEdges,
  setActivities: aiSetActivities,
  setWorkflowDataFields: aiSetWorkflowDataFields,
  setActivityDataFields: aiSetActivityDataFields,
  setRecordViews: aiSetRecordViews,
  setActivityTriggers: aiSetActivityTriggers,
  setActivityPermissions: aiSetActivityPermissions,
  setIsLoading: aiSetIsLoading,
  setActiveTab: aiSetActiveTab,
  setSelectedNode: aiSetSelectedNode,
  updateWorkflowState: aiUpdateWorkflowState,
  resetState: aiResetState,
} = aiBuilderSlice.actions;

// ─── Store ────────────────────────────────────────────────────────────────

export const store = configureStore({
  reducer: {
    workflow: workflowReducer,
    ui: uiSlice.reducer,
    views: viewsSlice.reducer,
    builder: aiBuilderSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Both dev checks deep-traverse the ENTIRE state tree on every dispatch.
      // `workflow.history.past` holds up to HISTORY_CAP (50) deep clones of the
      // whole diagram (workflowHistory.js `snapshotVM`), and a canvas drag
      // dispatches per mousemove — so leaving history in scope meant hundreds of
      // whole-tree deep walks per second over data nothing renders. Excluding it
      // is dev-only ergonomics; the snapshots are still plain JSON, still frozen
      // by immer, and every OTHER path stays checked.
      serializableCheck: {
        ignoredActions: ['ui/openConfirmation'],
        ignoredActionPaths: ['payload.onConfirm', 'payload.onSecondaryConfirm'],
        ignoredPaths: [
          'ui.confirmation.onConfirm',
          'ui.confirmation.onSecondaryConfirm',
          'workflow.history',
        ],
      },
      immutableCheck: { ignoredPaths: ['workflow.history'] },
    }),
  // `graphNodesChanged` / `graphEdgesChanged` fire once per mousemove during a
  // drag and (today) carry the full node/edge array as payload, so DevTools
  // would retain ~60 whole-graph copies per second alongside a state snapshot
  // each. Denylisting them keeps the action log readable and the tab from
  // ballooning; the reducers still run, and the post-drag state is visible on
  // the next non-denied action. Drop the denylist entry if you ever need to
  // time-travel through a drag specifically.
  devTools: {
    maxAge: 25,
    actionsDenylist: ['workflow/graphNodesChanged', 'workflow/graphEdgesChanged'],
  },
});
